/* Partner reviews: ratio-normalized editorial ratings; owner evidence remains separate. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.LinsoulPartnerReviews = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const FULL_REVIEWS = new Set(['review', 'multi-product review']);
  function validURL(value) {
    try { const u = new URL(value); return u.protocol === 'https:' && !u.username && !u.password; }
    catch (_) { return false; }
  }
  function visible(r, options = {}) {
    return Boolean(r && r.previewVisible === true && r.catalogStatus === 'active' &&
      r.exactModelMatch === true && validURL(r.sourceURL) &&
      (options.preview === true || r.approvedForProduction === true));
  }
  function normalizeScore(score, maximum) {
    if (typeof score !== 'number' || !Number.isFinite(score) ||
        typeof maximum !== 'number' || !Number.isFinite(maximum) || maximum <= 0 ||
        score < 0 || score > maximum) return null;
    // Use the same ratio for every scale. Do not shift a 1-5 source or floor zero to one.
    return 100 * score / maximum;
  }
  function normalized(r) { return r ? normalizeScore(r.score, r.scaleMax) : null; }
  function toStars(normalizedScore) {
    return typeof normalizedScore === 'number' && Number.isFinite(normalizedScore) &&
      normalizedScore >= 0 && normalizedScore <= 100 ? normalizedScore / 20 : null;
  }
  function stars(r) {
    return r && r.scoreKind === 'editorial' && validURL(r.scaleSourceURL) ? toStars(normalized(r)) : null;
  }
  function formatStars(value) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 5 ?
      String(Math.round(value * 100 + 1e-9) / 100) : null;
  }
  function starsHTML(value) {
    const text = formatStars(value);
    if (text === null) return '<span class="lr-unscored">Not rated</span>';
    const path = '<path d="m12 1.5 3.2 6.6 7.3 1.1-5.3 5.2 1.3 7.3-6.5-3.5-6.5 3.5 1.3-7.3L1.5 9.2l7.3-1.1Z"/>';
    const icons = Array.from({length:5}, (_,i) => {
      const fill = Math.max(0,Math.min(1,value-i));
      return '<span class="lr-star"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">'+path+'</svg>'+ 
        '<svg class="lr-star-fill" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style="clip-path:inset(0 '+((1-fill)*100).toFixed(4)+'% 0 0)">'+path+'</svg></span>';
    }).join('');
    return '<span class="lr-rating" role="img" aria-label="'+text+' out of 5 stars" data-rating-stars="'+value+'">'+
      '<span class="lr-rating-number" aria-hidden="true"><b>'+text+'</b><small>/ 5</small></span>'+ 
      '<span class="lr-stars" aria-hidden="true">'+icons+'</span></span>';
  }
  function reasons(r, options = {}) {
    const out = [];
    if (!visible(r, options)) out.push('Not approved for this view or not an active exact-model match.');
    if (r.sourceClass === 'brand-hosted') out.push('Brand-hosted content is not independent editorial evidence.');
    if (r.relationship === 'paid') out.push('Paid or sponsored coverage.');
    if (!FULL_REVIEWS.has(r.contentType)) out.push('Not a full editorial review.');
    if (r.scoreKind !== 'editorial') out.push('Not an overall numeric editorial score.');
    if (normalized(r) === null) out.push(r.score == null ? 'No numeric score.' : 'Rating scale not verified.');
    if (!r.scaleSourceURL || !validURL(r.scaleSourceURL)) out.push('No verified scale reference.');
    if (!r.publisherGroup) out.push('Publisher group unresolved.');
    if (r.rankingEligible !== true) out.push(...(r.rankingExclusions || ['Editorial review required.']));
    if (r.published && options.asOf && r.published > options.asOf) out.push('Publication date is after the snapshot.');
    return [...new Set(out)];
  }
  function aggregate(records, handle, options = {}) {
    // Never fuzzy-match a product generation, bundle or reservation listing.
    const all = records.filter(r => r.handle === handle && visible(r, options));
    const byPublisher = new Map();
    all.filter(r => reasons(r, options).length === 0).forEach(r => {
      const old = byPublisher.get(r.publisherGroup);
      const stamp = r.updated || r.published || '';
      const oldStamp = old && (old.updated || old.published || '');
      // Repeat publication/syndication cannot add a vote. Date wins, never the higher score.
      if (!old || stamp > oldStamp || (stamp === oldStamp && r.id.localeCompare(old.id) < 0)) byPublisher.set(r.publisherGroup, r);
    });
    const eligible = [...byPublisher.values()];
    const mean = eligible.length ? eligible.reduce((sum,r) => sum + normalized(r),0) / eligible.length : null;
    const minimum = Math.max(2, Number(options.minimum) || 2);
    return {handle, cards: all, sources: new Set(all.map(r=>r.publisherGroup)).size,
      eligible, scoredPublishers: eligible.length, normalizedMean: mean, stars: toStars(mean),
      rankable: eligible.length >= minimum,
      exclusions: all.filter(r=>reasons(r,options).length).map(r=>({id:r.id,reasons:reasons(r,options)}))};
  }
  function rank(records, options = {}) {
    const handles = [...new Set(records.filter(r=>visible(r,options)).map(r=>r.handle))];
    const summaries = handles.map(h=>aggregate(records,h,options));
    const ranked = summaries.filter(s=>s.rankable).sort((a,b)=>
      Number(formatStars(b.stars))-Number(formatStars(a.stars)) || a.handle.localeCompare(b.handle));
    let last = null, position = 0;
    ranked.forEach((s,i)=>{ const shown=formatStars(s.stars); if(shown!==last) position=i+1; s.rank=position; last=shown; });
    return {ranked, limited:summaries.filter(s=>!s.rankable).sort((a,b)=>a.handle.localeCompare(b.handle))};
  }
  function filter(records, state = {}, options = {}) {
    const q=(state.query||'').trim().toLowerCase();
    return records.filter(r=>visible(r,options)).filter(r=>
      (!state.product || r.handle===state.product) && (!state.publisher || r.publisherGroup===state.publisher) &&
      (!state.language || r.language===state.language) &&
      (!state.kind || (state.kind==='editorial' ? FULL_REVIEWS.has(r.contentType) && r.relationship!=='paid' :
       state.kind==='paid' ? r.relationship==='paid' : state.kind==='scored' ? reasons(r,options).length===0 :
       state.kind==='unscored' ? normalized(r)===null : true)) &&
      (!q || [r.product,r.publisher,r.author,r.positive,r.caution,r.summary,r.sourceTitleLabel].join(' ').toLowerCase().includes(q)));
  }
  return Object.freeze({visible, normalizeScore, normalized, toStars, stars, formatStars, starsHTML, reasons, aggregate, rank, filter, validURL});
});
